import { supabase } from './supabase';

// Helper function to convert images to JPEG format using FileReader
const convertImageToJPEG = (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (e) => {
      img.onload = () => {
        try {
          // Set canvas dimensions to image dimensions
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image on canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert to JPEG blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Create a new File object with JPEG type
              const jpegFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(jpegFile);
            } else {
              reject(new Error('Failed to convert image to JPEG'));
            }
          }, 'image/jpeg', 0.9); // 90% quality
        } catch (error) {
          reject(new Error('Failed to process image: ' + error.message));
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for conversion'));
      };
      
      // Use data URL instead of blob URL to avoid CSP issues
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    
    // Read the file as data URL
    reader.readAsDataURL(file);
  });
};

const adminGameService = {
  // Get all games (including drafts, pending, etc.) for admin
  getAllGames: async (filters = {}) => {
    try {
      let query = supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters if provided
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.category) {
        query = query.contains('game_category_mappings.game_categories.name', [filters.category]);
      }

      if (filters.studio) {
        query = query.eq('gaming_studios.name', filters.studio);
      }

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to load games.' };
    }
  },

  // Create a new game
  createGame: async (gameData) => {
    try {
      // Handle studio - gameData.studio should now be a studio ID (UUID)
      let studioId = null;
      if (gameData.studio) {
        // Check if it's a UUID (studio ID) or a name
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(gameData.studio);
        
        if (isUUID) {
          // It's a studio ID, verify it exists
          const { data: existingStudio } = await supabase
            .from('gaming_studios')
            .select('id')
            .eq('id', gameData.studio)
            .single();

          if (existingStudio) {
            studioId = gameData.studio;
          } else {
            return { success: false, error: `Studio with ID ${gameData.studio} not found` };
          }
        } else {
          // It's a studio name, find by name (legacy support)
          const { data: existingStudio } = await supabase
            .from('gaming_studios')
            .select('id')
            .eq('name', gameData.studio)
            .single();

          if (existingStudio) {
            studioId = existingStudio.id;
          } else {
            return { success: false, error: `Studio with name "${gameData.studio}" not found` };
          }
        }
      }

      // Prepare WebGPU URLs - use placeholders for directory-based games
      const isDirectoryBased = gameData.game_structure_type === 'directory';
      const webgpuUrls = {
        webgpu_url: gameData.webgpu_url || (isDirectoryBased ? 'directory-based-game' : null),
        webgpu_data_url: gameData.webgpu_data_url || (isDirectoryBased ? 'directory-based-game' : null),
        webgpu_wasm_url: gameData.webgpu_wasm_url || (isDirectoryBased ? 'directory-based-game' : null),
        webgpu_framework_url: gameData.webgpu_framework_url || (isDirectoryBased ? 'directory-based-game' : null),
        webgpu_loader_url: gameData.webgpu_loader_url || (isDirectoryBased ? 'directory-based-game' : null)
      };

      // Prepare the insert data
      const insertData = {
        title: gameData.title,
        description: gameData.description,
        studio_id: studioId,
        cover_image_url: gameData.thumbnail || gameData.cover_image_url,
        game_type: gameData.game_type || 'webgpu',
        game_structure_type: gameData.game_structure_type,
        game_directory_path: gameData.game_directory_path,
        game_manifest: gameData.game_manifest,
        rating: gameData.contentRating || 'E',
        status: gameData.status || 'draft',
        tier: gameData.tier || 'premium',
        price: gameData.price ? parseFloat(gameData.price) : 0,
        revenue_share_percentage: gameData.revenueShare || 70,
        estimated_play_time: gameData.estimated_play_time || 30,
        min_ram_gb: gameData.systemRequirements?.minRam ? parseInt(gameData.systemRequirements.minRam) : 4,
        min_storage_gb: gameData.systemRequirements?.minStorage ? parseInt(gameData.systemRequirements.minStorage) : 2,
        webgpu_required: gameData.systemRequirements?.webGpuSupport !== false,
        published_at: gameData.status === 'published' ? new Date().toISOString() : null
      };

      // Handle GameDistribution games vs WebGPU games
      if (gameData.game_type === 'gamedistribution') {
        // For GameDistribution games, add the URL and set WebGPU fields to null
        insertData.gamedistribution_url = gameData.gamedistribution_url;
        insertData.webgpu_url = null;
        insertData.webgpu_data_url = null;
        insertData.webgpu_wasm_url = null;
        insertData.webgpu_framework_url = null;
        insertData.webgpu_loader_url = null;
        insertData.webgpu_config = null;
      } else {
        // For WebGPU games, add WebGPU URLs and set GameDistribution URL to null
        insertData.gamedistribution_url = null;
        insertData.webgpu_url = gameData.webgpu_url || (isDirectoryBased ? 'directory-based-game' : null);
        insertData.webgpu_data_url = gameData.webgpu_data_url || (isDirectoryBased ? 'directory-based-game' : null);
        insertData.webgpu_wasm_url = gameData.webgpu_wasm_url || (isDirectoryBased ? 'directory-based-game' : null);
        insertData.webgpu_framework_url = gameData.webgpu_framework_url || (isDirectoryBased ? 'directory-based-game' : null);
        insertData.webgpu_loader_url = gameData.webgpu_loader_url || (isDirectoryBased ? 'directory-based-game' : null);
        insertData.webgpu_config = gameData.webgpu_config;
      }

      // Only add featured field if it's provided (to handle cases where column doesn't exist yet)
      if (gameData.featured !== undefined) {
        insertData.featured = gameData.featured;
      }

      // Create the game
      const { data: game, error: gameError } = await supabase
        .from('games')
        .insert(insertData)
        .select()
        .single();

      if (gameError) {
        return { success: false, error: `Failed to create game: ${gameError.message}` };
      }

      // Add category mapping if category is provided
      if (gameData.category) {
        const { data: category } = await supabase
          .from('game_categories')
          .select('id')
          .eq('name', gameData.category)
          .single();

        if (category) {
          await supabase
            .from('game_category_mappings')
            .insert({
              game_id: game.id,
              category_id: category.id
            });
        }
      }

      return { success: true, data: game };
    } catch (error) {
      return { success: false, error: 'Failed to create game.' };
    }
  },

  // Update an existing game
  updateGame: async (gameId, gameData) => {
    try {
      // Handle studio - gameData.studio should now be a studio ID (UUID)
      let studioId = null;
      if (gameData.studio) {
        // Check if it's a UUID (studio ID) or a name
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(gameData.studio);
        
        if (isUUID) {
          // It's a studio ID, verify it exists
          const { data: existingStudio } = await supabase
            .from('gaming_studios')
            .select('id')
            .eq('id', gameData.studio)
            .single();

          if (existingStudio) {
            studioId = gameData.studio;
          } else {
            return { success: false, error: `Studio with ID ${gameData.studio} not found` };
          }
        } else {
          // It's a studio name, find by name (legacy support)
          const { data: existingStudio } = await supabase
            .from('gaming_studios')
            .select('id')
            .eq('name', gameData.studio)
            .single();

          if (existingStudio) {
            studioId = existingStudio.id;
          } else {
            return { success: false, error: `Studio with name "${gameData.studio}" not found` };
          }
        }
      }

      // Update the game
      const updateData = {
        title: gameData.title,
        description: gameData.description,
        cover_image_url: gameData.thumbnail || gameData.cover_image_url,
        game_type: gameData.game_type || 'webgpu',
        rating: gameData.contentRating || 'E',
        status: gameData.status || 'draft',
        tier: gameData.tier || 'premium',
        price: gameData.price ? parseFloat(gameData.price) : 0,
        revenue_share_percentage: gameData.revenueShare || 70,
        estimated_play_time: gameData.estimated_play_time || 30,
        min_ram_gb: gameData.systemRequirements?.minRam ? parseInt(gameData.systemRequirements.minRam) : 4,
        min_storage_gb: gameData.systemRequirements?.minStorage ? parseInt(gameData.systemRequirements.minStorage) : 2,
        webgpu_required: gameData.systemRequirements?.webGpuSupport !== false,
        updated_at: new Date().toISOString()
      };

      // Handle GameDistribution games vs WebGPU games for updates
      if (gameData.game_type === 'gamedistribution') {
        // For GameDistribution games, update the URL and set WebGPU fields to null
        updateData.gamedistribution_url = gameData.gamedistribution_url;
        updateData.webgpu_url = null;
        updateData.webgpu_data_url = null;
        updateData.webgpu_wasm_url = null;
        updateData.webgpu_framework_url = null;
        updateData.webgpu_loader_url = null;
        updateData.webgpu_config = null;
      } else {
        // For WebGPU games, update WebGPU URLs and set GameDistribution URL to null
        updateData.gamedistribution_url = null;
        updateData.webgpu_url = gameData.webgpu_url;
        updateData.webgpu_data_url = gameData.webgpu_data_url;
        updateData.webgpu_wasm_url = gameData.webgpu_wasm_url;
        updateData.webgpu_framework_url = gameData.webgpu_framework_url;
        updateData.webgpu_loader_url = gameData.webgpu_loader_url;
        updateData.webgpu_config = gameData.webgpu_config;
      }

      // Only add featured field if it's provided (to handle cases where column doesn't exist yet)
      if (gameData.featured !== undefined) {
        updateData.featured = gameData.featured;
      }

      // Add directory-specific fields if provided
      if (gameData.game_structure_type) {
        updateData.game_structure_type = gameData.game_structure_type;
      }
      if (gameData.game_directory_path) {
        updateData.game_directory_path = gameData.game_directory_path;
      }
      if (gameData.game_subdirectory !== undefined) {
        updateData.game_subdirectory = gameData.game_subdirectory;
      }
      if (gameData.game_manifest) {
        updateData.game_manifest = gameData.game_manifest;
      }

      // Add screenshots if provided
      if (gameData.screenshots && Array.isArray(gameData.screenshots)) {
        updateData.screenshots = gameData.screenshots;
      }

      if (studioId) {
        updateData.studio_id = studioId;
      }

      // Set published_at if status is being changed to published
      if (gameData.status === 'published') {
        updateData.published_at = new Date().toISOString();
      }

      const { data: game, error: gameError } = await supabase
        .from('games')
        .update(updateData)
        .eq('id', gameId)
        .select()
        .single();

      if (gameError) {
        return { success: false, error: `Failed to update game: ${gameError.message}` };
      }

      // Update category mapping if category is provided
      if (gameData.category) {
        // Remove existing category mappings
        await supabase
          .from('game_category_mappings')
          .delete()
          .eq('game_id', gameId);

        // Add new category mapping
        const { data: category } = await supabase
          .from('game_categories')
          .select('id')
          .eq('name', gameData.category)
          .single();

        if (category) {
          await supabase
            .from('game_category_mappings')
            .insert({
              game_id: gameId,
              category_id: category.id
            });
        }
      }

      return { success: true, data: game };
    } catch (error) {
      return { success: false, error: 'Failed to update game.' };
    }
  },

  // Delete a game
  deleteGame: async (gameId) => {
    try {
      // Delete category mappings first
      await supabase
        .from('game_category_mappings')
        .delete()
        .eq('game_id', gameId);

      // Delete the game
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', gameId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete game.' };
    }
  },

  // Upload WebGPU game assets to Supabase Storage
  uploadWebGPUAssets: async (gameId, files) => {
    try {
      const bucketName = 'game-assets';
      const gameFolder = `game-${gameId}`;
      const uploadedUrls = {};

      for (const [fileType, file] of Object.entries(files)) {
        if (!file) continue;

        const fileName = `${gameFolder}/${fileType}`;
        
        // Delete existing file first
        await supabase.storage
          .from(bucketName)
          .remove([fileName]);

        // Determine content type and encoding
        let contentType = 'application/octet-stream';
        let contentEncoding = null;

        if (fileType.includes('wasm')) {
          contentType = 'application/wasm';
          contentEncoding = file.name.endsWith('.br') ? 'br' : null;
        } else if (fileType.includes('js') || fileType.includes('framework') || fileType.includes('loader')) {
          contentType = 'application/javascript';
          contentEncoding = file.name.endsWith('.br') ? 'br' : null;
        } else if (fileType.includes('data')) {
          contentType = 'application/octet-stream';
          contentEncoding = file.name.endsWith('.br') ? 'br' : null;
        } else if (fileType.includes('image') || file.type?.startsWith('image/')) {
          contentType = file.type || 'image/jpeg';
        }

        // Upload file
        const uploadOptions = {
          contentType,
          cacheControl: '3600',
          upsert: true
        };

        if (contentEncoding) {
          uploadOptions.metadata = {
            'content-encoding': contentEncoding
          };
        }

        const { data, error } = await supabase.storage
          .from(bucketName)
          .upload(fileName, file, uploadOptions);

        if (error) {
          console.error(`Error uploading ${fileType}:`, error);
          continue;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(fileName);

        uploadedUrls[fileType] = urlData.publicUrl;
      }

      return { success: true, data: uploadedUrls };
    } catch (error) {
      return { success: false, error: 'Failed to upload assets.' };
    }
  },

  // Upload directory-based WebGPU game
  uploadDirectoryGame: async (gameId, uploadData) => {
    try {
      const bucketName = 'game-assets';
      const gameFolder = `game-${gameId}`;
      const { files, manifest } = uploadData;
      
      // Track upload progress
      let uploadedCount = 0;
      let failedCount = 0;
      const totalFiles = files.length;
      const failedFiles = [];
      
      // Upload all files preserving directory structure
      for (const file of files) {
        const filePath = `${gameFolder}/${file.webkitRelativePath}`;
        
        // Determine content type and encoding - use safer content types for Supabase
        let contentType = 'application/octet-stream'; // Default safe type
        let contentEncoding = null;
        
        const fileName = (file.name || '').toLowerCase();
        
        // Use safer content types that Supabase allows
        if (fileName.endsWith('.br')) {
          contentEncoding = 'br';
          if (fileName.includes('.wasm.br')) {
            contentType = 'application/wasm';
          } else if (fileName.includes('.js.br')) {
            contentType = 'application/javascript';
          } else {
            contentType = 'application/octet-stream'; // Safe fallback for compressed files
          }
        } else if (fileName.endsWith('.js')) {
          contentType = 'application/javascript';
        } else if (fileName.endsWith('.wasm')) {
          contentType = 'application/wasm';
        } else if (fileName.endsWith('.html') || fileName.endsWith('.htm')) {
          contentType = 'text/html';
        } else if (fileName.endsWith('.json')) {
          contentType = 'application/json';
        } else if (fileName.endsWith('.webmanifest')) {
          contentType = 'application/json'; // Treat webmanifest as JSON
        } else if (fileName.endsWith('.css')) {
          // Use application/octet-stream for CSS to avoid MIME type restrictions
          contentType = 'application/octet-stream';
        } else if (fileName.endsWith('.png')) {
          contentType = 'image/png';
        } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
          contentType = 'image/jpeg';
        } else if (fileName.endsWith('.gif')) {
          contentType = 'image/gif';
        } else if (fileName.endsWith('.svg')) {
          contentType = 'image/svg+xml';
        } else if (fileName.endsWith('.ico')) {
          contentType = 'image/x-icon';
        } else if (fileName.endsWith('.txt')) {
          contentType = 'text/plain';
        } else {
          // For any other file type, use octet-stream
          contentType = 'application/octet-stream';
        }
        
        // Upload file with retry logic
        const uploadOptions = {
          contentType,
          cacheControl: '3600',
          upsert: true
        };
        
        if (contentEncoding) {
          uploadOptions.metadata = {
            'content-encoding': contentEncoding
          };
        }
        
        try {
          // CRITICAL FIX: Ensure we're uploading the actual file content, not the File object
          // The File object needs to be read properly for Supabase storage
          let fileContent = file;
          
          // If this is a File object from a web form, we need to handle it properly
          if (file instanceof File || (file && typeof file.arrayBuffer === 'function')) {
            // For File objects, use the file directly - Supabase handles File objects correctly
            fileContent = file;
          } else if (file && file.content) {
            // If the file has a content property, use that
            fileContent = file.content;
          } else if (typeof file === 'string') {
            // If it's a string, convert to buffer
            fileContent = Buffer.from(file, 'utf8');
          } else {
            // Log the issue and skip this file
            console.error(`Invalid file format for ${file.webkitRelativePath}:`, typeof file, file);
            failedFiles.push({
              path: file.webkitRelativePath,
              error: `Invalid file format: ${typeof file}`,
              contentType: contentType
            });
            failedCount++;
            continue;
          }
          
          const { error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, fileContent, uploadOptions);
          
          if (error) {
            console.error(`Error uploading ${file.webkitRelativePath}:`, error);
            failedFiles.push({
              path: file.webkitRelativePath,
              error: error.message,
              contentType: contentType
            });
            failedCount++;
          } else {
            uploadedCount++;
          }
        } catch (uploadError) {
          console.error(`Exception uploading ${file.webkitRelativePath}:`, uploadError);
          failedFiles.push({
            path: file.webkitRelativePath,
            error: uploadError.message,
            contentType: contentType
          });
          failedCount++;
        }
      }
      
      // Log summary
      console.log(`Upload summary: ${uploadedCount} successful, ${failedCount} failed out of ${totalFiles} total files`);
      if (failedFiles.length > 0) {
        console.log('Failed files:', failedFiles);
      }
      
      // Detect the actual directory structure by finding index.html
      let gameSubdirectory = null;
      let entryPoint = null;
      
      // Find index.html to determine the directory structure
      for (const file of files) {
        if ((file.name || '').toLowerCase() === 'index.html') {
          const pathParts = file.webkitRelativePath.split('/');
          if (pathParts.length > 1) {
            // File is in a subdirectory - preserve this structure
            const subdirectoryPath = pathParts.slice(0, -1).join('/');
            gameSubdirectory = subdirectoryPath;
            entryPoint = file.webkitRelativePath;
            console.log('Game entry point found:', entryPoint, 'in subdirectory:', gameSubdirectory);
            break;
          } else {
            // File is in root directory
            gameSubdirectory = '';
            entryPoint = 'index.html';
            console.log('Game entry point found in root directory');
            break;
          }
        }
      }
      
      // If no index.html found, this might be an issue
      if (gameSubdirectory === null) {
        console.warn('No index.html found in game files - this may cause loading issues');
        gameSubdirectory = '';
        entryPoint = 'index.html';
      }
      
      // Create enhanced manifest with detected structure
      // CRITICAL FIX: Don't spread array manifest - keep it as files property
      const enhancedManifest = {
        files: manifest, // Keep manifest as an array under 'files' property
        gameSubdirectory: gameSubdirectory,
        entryPoint: entryPoint,
        detectedAt: new Date().toISOString(),
        totalFiles: totalFiles,
        uploadedFiles: uploadedCount,
        failedFiles: failedCount
      };
      
      // Return the game directory path with enhanced information
      return { 
        success: true, 
        data: {
          directoryPath: gameFolder,
          gameSubdirectory: gameSubdirectory,
          entryPoint: entryPoint,
          manifest: enhancedManifest,
          uploadedFiles: uploadedCount,
          failedFiles: failedCount,
          totalFiles: totalFiles,
          failedFilesList: failedFiles
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to upload directory game.' };
    }
  },

  // Update existing game files (for game editor)
  updateGameFiles: async (gameId, uploadData) => {
    try {
      const bucketName = 'game-assets';
      const gameFolder = `game-${gameId}`;
      const { files, manifest } = uploadData;
      
      // First, clear existing game files to avoid conflicts
      console.log(`Clearing existing files for game ${gameId}...`);
      
      // List all existing files in the game folder
      const { data: existingFiles, error: listError } = await supabase.storage
        .from(bucketName)
        .list(gameFolder, {
          limit: 1000,
          sortBy: { column: 'name', order: 'asc' }
        });
      
      if (listError) {
        console.warn('Could not list existing files:', listError.message);
      } else if (existingFiles && existingFiles.length > 0) {
        // Delete existing files
        const filesToDelete = existingFiles.map(file => `${gameFolder}/${file.name}`);
        const { error: deleteError } = await supabase.storage
          .from(bucketName)
          .remove(filesToDelete);
        
        if (deleteError) {
          console.warn('Some existing files could not be deleted:', deleteError.message);
        } else {
          console.log(`Deleted ${filesToDelete.length} existing files`);
        }
      }
      
      // Track upload progress
      let uploadedCount = 0;
      let failedCount = 0;
      const totalFiles = files.length;
      const failedFiles = [];
      
      console.log(`Starting upload of ${totalFiles} new files...`);
      
      // Detect subdirectory structure from uploaded files
      let gameSubdirectory = null;
      let entryPoint = null;
      
      console.log(`Processing game directory structure for ${totalFiles} files...`);
      
      // Find index.html to determine the actual directory structure
      for (const file of files) {
        if ((file.name || '').toLowerCase() === 'index.html') {
          const pathParts = file.webkitRelativePath.split('/');
          console.log('Found index.html with path parts:', pathParts);
          
          if (pathParts.length > 1) {
            // File is in a subdirectory - preserve this structure
            const subdirectoryPath = pathParts.slice(0, -1).join('/');
            gameSubdirectory = subdirectoryPath;
            entryPoint = file.webkitRelativePath;
            console.log(`✅ Entry point found in subdirectory: "${gameSubdirectory}" -> full path: "${entryPoint}"`);
            break;
          } else {
            // File is in root directory
            gameSubdirectory = '';
            entryPoint = 'index.html';
            console.log('✅ Entry point found in root directory');
            break;
          }
        }
      }
      
      // If no index.html found, this might be an issue
      if (gameSubdirectory === null) {
        console.warn('⚠️ No index.html found in game files - this may cause loading issues');
        gameSubdirectory = '';
        entryPoint = 'index.html';
      }
      
      // Upload all new files preserving directory structure (now flattened if needed)
      for (const file of files) {
        const filePath = `${gameFolder}/${file.webkitRelativePath}`;
        
        // Determine content type and encoding - use safer content types for Supabase
        let contentType = 'application/octet-stream'; // Default safe type
        let contentEncoding = null;
        
        const fileName = (file.name || '').toLowerCase();
        
        // Use safer content types that Supabase allows
        if (fileName.endsWith('.br')) {
          contentEncoding = 'br';
          if (fileName.includes('.wasm.br')) {
            contentType = 'application/wasm';
          } else if (fileName.includes('.js.br')) {
            contentType = 'application/javascript';
          } else {
            contentType = 'application/octet-stream'; // Safe fallback for compressed files
          }
        } else if (fileName.endsWith('.js')) {
          contentType = 'application/javascript';
        } else if (fileName.endsWith('.wasm')) {
          contentType = 'application/wasm';
        } else if (fileName.endsWith('.html') || fileName.endsWith('.htm')) {
          contentType = 'text/html';
        } else if (fileName.endsWith('.json')) {
          contentType = 'application/json';
        } else if (fileName.endsWith('.webmanifest')) {
          contentType = 'application/json'; // Treat webmanifest as JSON
        } else if (fileName.endsWith('.css')) {
          // Use application/octet-stream for CSS to avoid MIME type restrictions
          contentType = 'application/octet-stream';
        } else if (fileName.endsWith('.png')) {
          contentType = 'image/png';
        } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
          contentType = 'image/jpeg';
        } else if (fileName.endsWith('.gif')) {
          contentType = 'image/gif';
        } else if (fileName.endsWith('.svg')) {
          contentType = 'image/svg+xml';
        } else if (fileName.endsWith('.ico')) {
          contentType = 'image/x-icon';
        } else if (fileName.endsWith('.txt')) {
          contentType = 'text/plain';
        } else {
          // For any other file type, use octet-stream
          contentType = 'application/octet-stream';
        }
        
        // Upload file with retry logic
        const uploadOptions = {
          contentType,
          cacheControl: '3600',
          upsert: true
        };
        
        if (contentEncoding) {
          uploadOptions.metadata = {
            'content-encoding': contentEncoding
          };
        }
        
        try {
          const { error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, file, uploadOptions);
          
          if (error) {
            console.error(`Error uploading ${file.webkitRelativePath}:`, error);
            failedFiles.push({
              path: file.webkitRelativePath,
              error: error.message,
              contentType: contentType
            });
            failedCount++;
          } else {
            uploadedCount++;
          }
        } catch (uploadError) {
          console.error(`Exception uploading ${file.webkitRelativePath}:`, uploadError);
          failedFiles.push({
            path: file.webkitRelativePath,
            error: uploadError.message,
            contentType: contentType
          });
          failedCount++;
        }
      }
      
      // Log summary
      console.log(`Upload summary: ${uploadedCount} successful, ${failedCount} failed out of ${totalFiles} total files`);
      if (failedFiles.length > 0) {
        console.log('Failed files:', failedFiles);
      }
      
      
      // Create enhanced manifest with detected structure
      // CRITICAL FIX: Don't spread array manifest - keep it as files property
      const enhancedManifest = {
        files: manifest, // Keep manifest as an array under 'files' property
        gameSubdirectory: gameSubdirectory,
        entryPoint: entryPoint,
        detectedAt: new Date().toISOString(),
        totalFiles: totalFiles,
        uploadedFiles: uploadedCount,
        failedFiles: failedCount,
        updatedAt: new Date().toISOString()
      };
      
      // CRITICAL FIX: Automatically update the game record with detected subdirectory
      // This prevents the game_subdirectory field from being lost during file updates
      console.log(`🔄 Updating game ${gameId} database record...`);
      console.log(`   - Subdirectory: "${gameSubdirectory}"`);
      console.log(`   - Entry point: "${entryPoint}"`);
      
      const gameUpdateData = {
        game_subdirectory: gameSubdirectory,
        game_manifest: enhancedManifest,
        updated_at: new Date().toISOString()
      };
      
      console.log('Database update payload:', JSON.stringify(gameUpdateData, null, 2));
      
      const { data: updatedGame, error: updateError } = await supabase
        .from('games')
        .update(gameUpdateData)
        .eq('id', gameId)
        .select('id, game_subdirectory, game_manifest')
        .single();
      
      if (updateError) {
        console.error('❌ Failed to update game subdirectory in database:', updateError.message);
        console.error('Update error details:', updateError);
        // Don't fail the entire operation, but log the error
        return { 
          success: false, 
          error: `File upload succeeded but failed to update game subdirectory: ${updateError.message}` 
        };
      }
      
      console.log(`✅ Successfully updated game ${gameId} in database`);
      console.log('Updated game record:', updatedGame);
      console.log(`   - Subdirectory in DB: "${updatedGame.game_subdirectory}"`);
      console.log(`   - Manifest in DB:`, updatedGame.game_manifest);
      
      // Return the game directory path with enhanced information
      return { 
        success: true, 
        data: {
          directoryPath: gameFolder,
          gameSubdirectory: gameSubdirectory,
          entryPoint: entryPoint,
          manifest: enhancedManifest,
          uploadedFiles: uploadedCount,
          failedFiles: failedCount,
          totalFiles: totalFiles,
          failedFilesList: failedFiles
        }
      };
    } catch (error) {
      console.error('Error in updateGameFiles:', error);
      return { success: false, error: `Failed to update game files: ${error.message}` };
    }
  },

  // Upload game images (thumbnails, screenshots)
  uploadGameImages: async (gameId, images) => {
    try {
      const bucketName = 'game-assets';
      const gameFolder = `game-${gameId}/images`;
      const uploadedUrls = {};

      for (const [imageType, image] of Object.entries(images)) {
        if (!image) continue;

        // Convert image to JPEG if it's not already
        let processedImage = image;
        let fileName = `${gameFolder}/${imageType}-${Date.now()}.jpg`;
        
        // If the image is PNG or other format, convert to JPEG
        if (image.type !== 'image/jpeg') {
          try {
            processedImage = await convertImageToJPEG(image);
          } catch (conversionError) {
            console.error(`Failed to convert ${imageType} to JPEG:`, conversionError);
            // Skip this image if conversion fails
            continue;
          }
        }
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .upload(fileName, processedImage, {
            contentType: 'image/jpeg',
            cacheControl: '3600',
            upsert: true
          });

        if (error) {
          console.error(`Error uploading ${imageType}:`, error);
          continue;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(fileName);

        uploadedUrls[imageType] = urlData.publicUrl;
      }

      return { success: true, data: uploadedUrls };
    } catch (error) {
      return { success: false, error: 'Failed to upload images.' };
    }
  },

  // Bulk update games
  bulkUpdateGames: async (gameIds, updates) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .in('id', gameIds)
        .select();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to bulk update games.' };
    }
  },

  // Get game analytics
  getGameAnalytics: async (gameId) => {
    try {
      // Get basic game stats
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('*')
        .eq('id', gameId)
        .single();

      if (gameError) {
        return { success: false, error: gameError.message };
      }

      // Get play sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_game_sessions')
        .select('*')
        .eq('game_id', gameId);

      // Get ratings
      const { data: ratings, error: ratingsError } = await supabase
        .from('user_game_ratings')
        .select('*')
        .eq('game_id', gameId);

      // Get favorites
      const { data: favorites, error: favoritesError } = await supabase
        .from('user_favorites')
        .select('*')
        .eq('game_id', gameId);

      const analytics = {
        game,
        totalSessions: sessions?.length || 0,
        totalRatings: ratings?.length || 0,
        averageRating: ratings?.length > 0 
          ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length 
          : 0,
        totalFavorites: favorites?.length || 0,
        playCount: game.play_count || 0
      };

      return { success: true, data: analytics };
    } catch (error) {
      return { success: false, error: 'Failed to load analytics.' };
    }
  },

  // Get all gaming studios
  getGamingStudios: async () => {
    try {
      const { data, error } = await supabase
        .from('gaming_studios')
        .select('*')
        .order('name');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to load studios.' };
    }
  },

  // Get all game categories
  getGameCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('game_categories')
        .select('*')
        .order('name');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to load categories.' };
    }
  },

  // Upload React game from built files
  uploadReactGame: async (gameData, builtFilesPath) => {
    try {
      // First create the game entry
      const gameResult = await this.createGame({
        ...gameData,
        game_type: 'react',
        game_structure_type: 'directory',
        // Set placeholder URLs for React games
        webgpu_url: 'directory-based-game',
        webgpu_data_url: 'directory-based-game',
        webgpu_wasm_url: 'directory-based-game',
        webgpu_framework_url: 'directory-based-game',
        webgpu_loader_url: 'directory-based-game'
      });

      if (!gameResult.success) {
        return gameResult;
      }

      const game = gameResult.data;
      const gameId = game.id;

      // Read the built files from the specified path
      const fs = require('fs');
      const path = require('path');
      
      const readDirectoryRecursively = (dirPath, basePath = '') => {
        const files = [];
        const items = fs.readdirSync(dirPath);
        
        for (const item of items) {
          const fullPath = path.join(dirPath, item);
          const relativePath = basePath ? `${basePath}/${item}` : item;
          const stat = fs.statSync(fullPath);
          
          if (stat.isDirectory()) {
            files.push(...readDirectoryRecursively(fullPath, relativePath));
          } else {
            const fileContent = fs.readFileSync(fullPath);
            files.push({
              name: item,
              webkitRelativePath: relativePath,
              type: this.getMimeType(item),
              size: stat.size,
              content: fileContent
            });
          }
        }
        
        return files;
      };

      // Read all files from the built directory
      const files = readDirectoryRecursively(builtFilesPath);
      
      // Create manifest
      const manifest = {
        files: files.map(f => ({ path: f.webkitRelativePath, size: f.size })),
        totalFiles: files.length,
        gameType: 'react',
        buildPath: builtFilesPath,
        entryPoint: 'index.html',
        gameSubdirectory: '', // React builds are typically in root
        createdAt: new Date().toISOString()
      };

      // Upload the directory
      const uploadResult = await this.uploadDirectoryGame(gameId, { files, manifest });
      
      if (!uploadResult.success) {
        // Clean up the game entry if upload failed
        await this.deleteGame(gameId);
        return uploadResult;
      }

      // Update the game with directory information
      const updateResult = await this.updateGame(gameId, {
        ...gameData,
        game_directory_path: uploadResult.data.directoryPath,
        game_subdirectory: uploadResult.data.gameSubdirectory || '',
        game_manifest: uploadResult.data.manifest
      });

      if (!updateResult.success) {
        return updateResult;
      }

      return { 
        success: true, 
        data: {
          game: updateResult.data,
          uploadInfo: uploadResult.data
        }
      };
    } catch (error) {
      console.error('Error uploading React game:', error);
      return { success: false, error: 'Failed to upload React game.' };
    }
  },

  // Helper method to get MIME type based on file extension
  getMimeType: (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const mimeTypes = {
      'html': 'text/html',
      'js': 'application/javascript',
      'css': 'text/css',
      'json': 'application/json',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'svg': 'image/svg+xml',
      'ico': 'image/x-icon',
      'woff': 'font/woff',
      'woff2': 'font/woff2',
      'ttf': 'font/ttf',
      'eot': 'application/vnd.ms-fontobject'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  }
};

export default adminGameService;
